import React, { useRef, useState, useEffect } from 'react';
import { Heading } from 'components/components/atoms';
import styled from 'styled-components';
import { isValidTitle } from 'utils/validateTitle';
import cx from 'classnames';
import useNewColorSchemaOptions from 'utils/hooks/useNewColorSchemaOptions';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import useHasMounted from 'utils/hooks/useHasMounted';
import breakpoints from 'utils/breakpoints';
import {
  getPlainText,
  getMaxCharacterCount,
  truncateHTMLContent,
} from 'utils/truncateRichText';

const ImageContainer = styled.div`
  @media ${breakpoints.md} {
    padding-right: 3.5rem;
  }
`;
const SummaryContainer = styled.div`
  @media ${breakpoints.md} {
    padding-right: 3.5rem;
  }
`;
const SummaryCopy = styled.div`
  ${({ showNewSchemaColor, newSchemaTextColorHexaCode }) =>
    showNewSchemaColor && `color: ${newSchemaTextColorHexaCode};`}
`;
const MoreSummary = styled.span`
  ${({ showNewSchemaColor, newSchemaTextColorHexaCode }) =>
    showNewSchemaColor && `color: ${newSchemaTextColorHexaCode};`}
`;

const SummaryHeading = styled(Heading)`
  margin-bottom: 0 !important;
  font-size: 2.375rem;
  line-height: 1.1;
  letter-spacing: 0;

  @media ${breakpoints.lg} {
    font-size: 2.125rem;
  }
`;

const Img = styled.img`
  max-height: 9.5625rem;
  max-width: 12rem;
  width: auto;
  height: auto;
  object-fit: contain;
`;

const CommunityProductOverviewSummary = ({ productOverviewView }) => {
  const hasMounted = useHasMounted();
  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const max_caracter_count = getMaxCharacterCount(breakpoint);

  const { newSchemaTextColorHexaCode, showNewSchemaColor } =
    useNewColorSchemaOptions();

  const textAlignment = productOverviewView.textAlignment;

  const withLogo = productOverviewView.image && productOverviewView.image.src;
  const imgRef = useRef();
  useEffect(() => {
    // Image load is observed for downstream measurements; no state needed
    // since the new layout sizes the image purely via max-height.
  }, [imgRef]);

  //render Summary section
  const [openMore, setOpenMore] = useState(false);
  const getMoreSpan = (textContent, htmlContent) => {
    const componentClassName = 'product-summary-component';

    const hyperlinkStyle = `
    .${componentClassName} a {
      font-weight: bold !important;
      text-decoration: underline !important;
    }
  `;

    // truncateHTMLContent() walks the DOM, so it cannot run on the server. Use
    // the plain-text truncation for the first render on BOTH sides, then upgrade
    // to the markup-preserving version once mounted.
    const truncatedHTML = hasMounted
      ? `<style>${hyperlinkStyle}</style>${truncateHTMLContent(
          htmlContent,
          max_caracter_count - 3,
        )}`
      : textContent.substring(0, max_caracter_count - 3) + '...';

    return (
      <div className={componentClassName}>
        <span dangerouslySetInnerHTML={{ __html: truncatedHTML }} />
        {/* Full copy kept in the server-rendered HTML for crawlers; hidden
            until More+ swaps to getLessSpan(). */}
        <span hidden dangerouslySetInnerHTML={{ __html: htmlContent }} />
        <MoreSummary
          className={cx('font-graphie font-bold cursor-pointer', {
            'text-mattamy-blue ': !showNewSchemaColor,
          })}
          aria-label={openMore ? 'Show less' : 'Show more'}
          aria-expanded={openMore}
          as={'button'}
          tabIndex='0'
          onClick={() => {
            setOpenMore(true);
          }}
          onKeyUp={(e) => {
            if (e.key === 'Enter') setOpenMore(true);
          }}
        >
          {'More+'}
        </MoreSummary>
      </div>
    );
  };

  const getLessSpan = (htmlContent) => {
    const componentClassName = 'product-summary-component';

    const styledHtmlContent = `
    <style>
      .${componentClassName} a {
        font-weight: bold !important;
        text-decoration: underline !important;
      }
    </style>
    ${htmlContent}
  `;

    return (
      <div className={componentClassName}>
        <span dangerouslySetInnerHTML={{ __html: styledHtmlContent }} />
        <MoreSummary
          className={cx('font-graphie font-bold cursor-pointer ml-1', {
            'text-mattamy-blue ': !showNewSchemaColor,
          })}
          showNewSchemaColor={showNewSchemaColor}
          newSchemaTextColorHexaCode={newSchemaTextColorHexaCode}
          aria-label={openMore ? 'Show less' : 'Show more'}
          aria-expanded={openMore}
          as={'button'}
          tabIndex='0'
          onClick={() => {
            setOpenMore(false);
          }}
          onKeyUp={(e) => {
            if (e.key === 'Enter') setOpenMore(false);
          }}
        >
          {' Less-'}
        </MoreSummary>
      </div>
    );
  };

  const renderSummary = () => {
    const productCopy = productOverviewView.copy;
    // Derived without the DOM so the server produces the same value as the
    // client. This previously used document.createElement(), which is undefined
    // in Node, so copyText was null and every branch below rendered an empty
    // <p> server-side while the client rendered real content (React #418).
    const copyText = getPlainText(productCopy);

    const componentClassName = 'product-summary-component';

    const shortContentWithStyledLinks = `
    <style>
      .${componentClassName} a {
        font-weight: bold !important;
        text-decoration: underline !important;
      }
    </style>
    ${productCopy}
  `;

    return (
      <div className='w-full flex flex-col md:flex-row md:items-stretch'>
        {withLogo && (
          <ImageContainer className='flex-shrink-0 flex items-center justify-center mb-6 md:mb-0 '>
            <Img
              ref={imgRef}
              src={productOverviewView.image.src}
              alt={productOverviewView.image.alt}
            />
          </ImageContainer>
        )}

        <SummaryContainer className='flex flex-col justify-center gap-2 w-full'>
          {isValidTitle(productOverviewView.headline) && (
            <SummaryHeading
              as='h2'
              color={showNewSchemaColor ? newSchemaTextColorHexaCode : 'blue'}
              className={cx('font-trade-gothic-20 text-mattamy-blue', {
                'text-left': textAlignment === 'left' || !textAlignment,
                'text-center': textAlignment === 'center',
                'text-right': textAlignment === 'right',
              })}
            >
              {productOverviewView.headline}
            </SummaryHeading>
          )}
          <SummaryCopy
            className={cx('text-normal font-graphie w-full leading-7 mt-4', {
              'text-mattamy-gray': !showNewSchemaColor,
              'text-left': textAlignment === 'left' || !textAlignment,
              'text-center': textAlignment === 'center',
              'text-right': textAlignment === 'right',
            })}
            showNewSchemaColor={showNewSchemaColor}
            newSchemaTextColorHexaCode={newSchemaTextColorHexaCode}
          >
            {copyText && copyText.length > max_caracter_count && !openMore ? (
              getMoreSpan(copyText, productCopy)
            ) : (
              <p></p>
            )}
            {copyText && copyText.length > max_caracter_count && openMore ? (
              getLessSpan(productCopy)
            ) : (
              <p></p>
            )}
            {copyText && copyText.length <= max_caracter_count ? (
              <div
                className={componentClassName}
                dangerouslySetInnerHTML={{
                  __html: shortContentWithStyledLinks,
                }}
              />
            ) : (
              <p></p>
            )}
          </SummaryCopy>
        </SummaryContainer>
      </div>
    );
  };

  return <>{renderSummary()}</>;
};

export default CommunityProductOverviewSummary;
